import { filterQuestions } from '../data/questions';
import type { Difficulty } from '../data/types';
import { getHighScore, recordScore } from '../game/highscore';
import { Quiz, type PlayQuestion } from '../game/quiz';
import type { Campfire } from './campfire';

type DiffChoice = Difficulty | 'all';

const REVEAL_MS = 1150;

export class GameApp {
  private quiz: Quiz | null = null;
  private current: PlayQuestion | null = null;
  private diffChoice: DiffChoice = 'all';
  private locked = false;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly campfire: Campfire,
  ) {}

  start(): void {
    this.renderTitle();
  }

  // ---- Title -------------------------------------------------------------

  private renderTitle(): void {
    this.detachKeys();
    this.campfire.setIntensity(1);
    const best = getHighScore();
    this.root.innerHTML = `
      <section class="screen" aria-live="polite">
        <h1 class="title">Campfire Trivia</h1>
        <p class="tagline">Settle in by the fire and test your camping know-how — keep your streak alive, one wrong answer and the embers fade.</p>
        ${best > 0 ? `<p class="best">🔥 Best streak: ${best}</p>` : ''}
        <div class="options">
          <div>
            <div class="option-label">Difficulty</div>
            <div class="segmented" data-group="difficulty">
              <button data-diff="all" aria-pressed="${this.diffChoice === 'all'}">Any</button>
              <button data-diff="easy" aria-pressed="${this.diffChoice === 'easy'}">Easy</button>
              <button data-diff="medium" aria-pressed="${this.diffChoice === 'medium'}">Medium</button>
              <button data-diff="hard" aria-pressed="${this.diffChoice === 'hard'}">Hard</button>
            </div>
          </div>
        </div>
        <button class="btn-primary" data-action="start">Light the fire</button>
      </section>`;

    this.root.querySelectorAll<HTMLButtonElement>('[data-diff]').forEach((b) =>
      b.addEventListener('click', () => {
        this.diffChoice = b.dataset.diff as DiffChoice;
        this.renderTitle();
      }),
    );
    this.root
      .querySelector<HTMLButtonElement>('[data-action="start"]')
      ?.addEventListener('click', () => this.startRun());
  }

  // ---- Run ---------------------------------------------------------------

  private resolvePool() {
    const exact = filterQuestions({ difficulty: this.diffChoice });
    return exact.length >= 4 ? exact : filterQuestions();
  }

  private startRun(): void {
    const pool = this.resolvePool();
    this.quiz = new Quiz(pool);
    this.locked = false;
    this.nextQuestion();
  }

  private nextQuestion(): void {
    if (!this.quiz) return;
    this.current = this.quiz.next();
    this.locked = false;
    this.renderQuestion();
  }

  private renderQuestion(): void {
    if (!this.quiz || !this.current) return;
    const pq = this.current;
    const score = this.quiz.score;
    this.campfire.setIntensity(1 + Math.min(score / 12, 1.4));

    this.root.innerHTML = `
      <section class="screen">
        <div class="hud">
          <span>🔥 Campfire Trivia</span>
          <span class="streak">Streak <strong>${score}</strong></span>
        </div>
        <div class="qcard">
          <div class="qmeta">${escape(pq.question.category)} · ${pq.question.difficulty}</div>
          <p class="qtext">${escape(pq.question.question)}</p>
          <div class="choices" role="group" aria-label="Answer choices">
            ${pq.choices
              .map(
                (c, i) =>
                  `<button class="choice" data-choice="${i}">${i + 1}. ${escape(c)}</button>`,
              )
              .join('')}
          </div>
        </div>
      </section>`;

    const buttons = Array.from(this.root.querySelectorAll<HTMLButtonElement>('.choice'));
    buttons.forEach((btn) =>
      btn.addEventListener('click', () => this.onAnswer(pq.choices[Number(btn.dataset.choice)])),
    );

    this.detachKeys();
    this.keyHandler = (e: KeyboardEvent) => {
      const n = Number(e.key);
      if (n >= 1 && n <= pq.choices.length) this.onAnswer(pq.choices[n - 1]);
    };
    globalThis.addEventListener('keydown', this.keyHandler);
  }

  private onAnswer(choice: string): void {
    if (!this.quiz || !this.current || this.locked) return;
    this.locked = true;
    this.detachKeys();
    const correct = this.quiz.answer(choice, this.current);
    const correctAnswer = this.current.question.correctAnswer;

    this.root.querySelectorAll<HTMLButtonElement>('.choice').forEach((btn) => {
      btn.disabled = true;
      const label = this.current!.choices[Number(btn.dataset.choice)];
      if (label === correctAnswer) btn.classList.add('correct');
      else if (label === choice && !correct) btn.classList.add('wrong');
    });

    globalThis.setTimeout(() => {
      if (correct) this.nextQuestion();
      else this.renderGameOver();
    }, REVEAL_MS);
  }

  // ---- Game over ---------------------------------------------------------

  private renderGameOver(): void {
    this.detachKeys();
    const score = this.quiz?.score ?? 0;
    const prevBest = getHighScore();
    const best = recordScore(score);
    const isNewBest = score > 0 && score >= best && score > prevBest;
    this.campfire.setIntensity(0.45);

    this.root.innerHTML = `
      <section class="screen gameover" aria-live="polite">
        <h1 class="title">The fire dims…</h1>
        <p class="score-caption">Your streak</p>
        <div class="final-score">${score}</div>
        ${
          isNewBest
            ? `<p class="new-best">✨ New best streak!</p>`
            : `<p class="best">🔥 Best streak: ${best}</p>`
        }
        <button class="btn-primary" data-action="again">Stoke it again</button>
      </section>`;

    this.root
      .querySelector<HTMLButtonElement>('[data-action="again"]')
      ?.addEventListener('click', () => this.renderTitle());
  }

  private detachKeys(): void {
    if (this.keyHandler) {
      globalThis.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
  }
}

/** Escape user-facing text before injecting into innerHTML. */
function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
